<template>
  <div>
    <c-eth-gas-option
      type="stake"
      :gasLimit="gasLimit"
      @update="updateGasPrices"
    >
      <div class="d-flex mt-16">
        <div class="flex-1">
          <div class="text-5 font-weight-medium">Locked Reward</div>
          <div class="d-flex align-center mt-2 text-2">
            <div>
              <span class="primary--text">
                {{ lockedReward | denomFormat }}</span
              >
              <span class="gray--text text-uppercase ml-1">{{
                currentProject.staking.token
              }}</span>
            </div>
            <img
              :src="
                ($vuetify.theme.dark
                  ? currentProject.logoSmallDark
                  : currentProject.logoSmall) || currentProject.logoSmall
              "
              height="36"
              width="36"
              class="ml-4"
            />
          </div>
          <div class="mt-2 gray--text text-5">${{ lockedAmountUsd | currency }}</div>
        </div>
        <div class="flex-1">
          <div class="text-5 font-weight-medium">Claimable Reward</div>
          <div class="d-flex align-center mt-2 text-2">
            <div>
              <span class="primary--text">
                {{ totalAmount | denomFormat }}</span
              >
              <span class="gray--text text-uppercase ml-1">{{
                currentProject.staking.token
              }}</span>
            </div>
            <img
              :src="
                ($vuetify.theme.dark
                  ? currentProject.logoSmallDark
                  : currentProject.logoSmall) || currentProject.logoSmall
              "
              height="36"
              width="36"
              class="ml-4"
            />
          </div>
          <div class="mt-2 gray--text text-5">${{ amountUsd | currency }}</div>
        </div>
      </div>
      <div v-if="claimable" class="mt-10">
        <div class="text-5 font-weight-medium">Transaction Fee</div>
        <div class="d-flex align-center mt-4 text-2">
          <div>
            <span class="primary--text">{{ txFees.amount | denomFormat }}</span>
            <span class="gray--text text-uppercase ml-1">ETH</span>
          </div>
        </div>
        <div class="mt-2 gray--text text-5">${{ txFees.value | currency }}</div>
      </div>
    </c-eth-gas-option>
    <div class="d-flex mt-16">
      <div class="flex-1">
        <c-btn
          @click="unlock"
          walletRequired
          :disabled="buttonDisabled || !unlockable"
          is-secondary
          class="width-100"
          >Unlock to claim</c-btn
        >
      </div>
      <div class="flex-1 ml-2">
        <c-btn
          class="width-100"
          @click="claim"
          :disabled="
            Number(rawTx.gas || 0) <= 0 || !claimable || buttonDisabled
          "
          walletRequired
          >Claim</c-btn
        >
      </div>
    </div>
    <tx-ethereum
      v-if="showTxModal"
      title="Claim Rewards"
      name="RedeemReward"
      successMesssage="Transaction sent"
      broadcastAction="staking/celer/broadcastTx"
      :txObject="txObject"
      :txFees="txFees"
      :metadata="{ amount: totalAmount }"
      @cancel="showTxModal = false"
      @onSuccess="onTxSuccess"
    >
      <div class="d-flex mt-3">
        <div
          class="flex-2 text-left text-5 font-weight-medium text-uppercase gray--text"
        >
          Amount:
        </div>
        <div class="flex-3 text-3">
          {{ totalAmount | denomFormat }}
          {{ currentProject.staking.token | uppercase }}
        </div>
      </div>
    </tx-ethereum>
    <c-loading-dialog
      v-if="unlockingReward"
      title="Unlocking Reward"
      subTitle="Your staking reward is being unlocked, which might take a few minutes."
    />
  </div>
</template>

<script>
import BN from "bignumber.js";
import { mapGetters } from "vuex";

export default {
  data: () => ({
    gasPrices: {},
    gasLimit: 0,
    rawTx: {},
    showTxModal: false,
    unlockingReward: false,
  }),
  computed: {
    ...mapGetters({
      walletSelected: "userWallets/walletSelected",
      getTokenPriceInUsd: "appConfig/getTokenPriceInUsd",
      currentProject: "projectConfig/latestProject",
      rewards: "staking/celer/rewards",
      stakedBalancesState: "staking/celer/stakeBalances",
    }),
    lockedReward() {
      return this.rewards.result?.lockedReward ?? 0;
    },
    claimableReward() {
      return this.rewards.result?.claimableReward ?? 0;
    },
    totalAmount() {
      return this.rewards.result?.claimableReward ?? 0;
    },
    unlockable() {
      return new BN(this.lockedReward).gte(1e-6);
    },
    claimable() {
      return new BN(this.claimableReward).gte(1e-6);
    },
    buttonDisabled() {
      if (!this.currentProject.status?.active || this.unlockingReward)
        return true;
    },
    amountUsd() {
      return (
        this.getTokenPriceInUsd(this.currentProject.staking.token) *
        this.totalAmount
      );
    },
    lockedAmountUsd() {
      return (
        this.getTokenPriceInUsd(this.currentProject.staking.token) *
        this.lockedReward
      );
    },
    txFees() {
      const amount = new BN(this.gasPrices.gas || 0)
        .multipliedBy(this.gasPrices.maxFeePerGas || 0)
        .dividedBy(1e18)
        .toNumber();
      return {
        amount,
        value: amount * this.getTokenPriceInUsd("eth"),
      };
    },
    txObject() {
      return {
        ...this.rawTx,
        ...this.gasPrices,
      };
    },
  },
  methods: {
    updateGasPrices(prices) {
      this.gasPrices = prices;
    },
    async getRawTx() {
      const rawTx = await this.$store.dispatch(
        "staking/celer/getClaimRewardsRawTx"
      );
      this.gasLimit = rawTx.gas;
      this.rawTx = rawTx;
    },
    async unlock() {
      this.unlockingReward = true;
      try {
        if (this.unlockable) {
          await this.$store.dispatch("staking/celer/initRedeemReward");
          await Promise.all([
            this.$store.dispatch("staking/celer/fetchRewards"),
            this.getRawTx(),
          ]);
          this.showTxModal = true;
        }
      } catch (error) {
        this.$store.dispatch("addAlert", {
          type: "error",
          title: "Claim Error",
          message: error.message,
        });
      } finally {
        this.unlockingReward = false;
      }
    },
    async claim() {
      this.showTxModal = true;
    },
    async onTxSuccess() {
      this.showTxModal = false;
      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
      });
      this.$emit("close");
    },
  },
  watch: {
    claimable: {
      immediate: true,
      handler(should) {
        if (!should) return;
        if (this.walletSelected?.address?.startsWith("0x")) {
          this.getRawTx();
        }
      },
    },
  },
};
</script>
